import { ArrowBack } from '@mui/icons-material';
import { Alert, CircularProgress, IconButton, Snackbar } from '@mui/material';
import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { MobileActionBar } from './fragments/MobileActionBar';
import { MobileAttachments } from './fragments/MobileAttachments';
import { MobileLocationSection } from './fragments/MobileLocationSection';
import { MobileReportedInfo } from './fragments/MobileReportedInfo';
import { MobileServiceHistory } from './fragments/MobileServiceHistory/MobileServiceHistory';
import { MobileServiceSummary } from './fragments/MobileServiceSummary/MobileServiceSummary';
import { MobileSLAHero } from './fragments/MobileSLAHero';
import {
  PageContainer,
  HeaderHeroWrapper,
  PageHeader,
  HeaderContent,
  HeaderRow,
  ServiceIdTitle,
  HeaderMeta,
  HeaderLocation,
  PriorityBadge,
  LoadingContainer,
  ErrorContainer,
  ErrorText,
  ContentContainer,
  TabContainer,
} from './MobileServiceDetail.styles';
import { useMobileDetailActions } from './useMobileDetailActions';
import { useMobileServiceDetail } from './useMobileServiceDetail';
import { Tabs } from '../../../components/molecules/Tabs';
import { COLORS } from '../../../configs/colors';
import { useGetUserSystemPermissions } from '../../../queries/user-query';

export const MobileServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const logoPic = (location.state as { logoPic?: string } | null)?.logoPic;
  const serviceId = id ? parseInt(id, 10) : NaN;
  const isValidId = !isNaN(serviceId) && serviceId > 0;

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const detail = useMobileServiceDetail(serviceId, isValidId);
  const actions = useMobileDetailActions({
    handleNoteRefresh: detail.handleNoteRefresh,
    handlePartActionRefresh: detail.handlePartActionRefresh,
    uploadFiles: detail.uploadFiles,
  });

  const handleBack = (): void => {
    navigate('/mobile/services');
  };

  if (detail.isLoadingService) {
    return (
      <PageContainer>
        <HeaderHeroWrapper>
          <PageHeader>
            <IconButton onClick={handleBack} sx={{ color: COLORS.White }} aria-label="Go back">
              <ArrowBack />
            </IconButton>
            <HeaderContent>
              <ServiceIdTitle>Loading...</ServiceIdTitle>
            </HeaderContent>
          </PageHeader>
        </HeaderHeroWrapper>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!isValidId || detail.isServiceError || !detail.service) {
    return (
      <PageContainer>
        <HeaderHeroWrapper>
          <PageHeader>
            <IconButton onClick={handleBack} sx={{ color: COLORS.White }} aria-label="Go back">
              <ArrowBack />
            </IconButton>
            <HeaderContent>
              <ServiceIdTitle>Error</ServiceIdTitle>
            </HeaderContent>
          </PageHeader>
        </HeaderHeroWrapper>
        <ErrorContainer>
          <ErrorText>
            {!isValidId ? 'Invalid service ID' : 'Failed to load service details'}
          </ErrorText>
        </ErrorContainer>
      </PageContainer>
    );
  }

  const { service } = detail;
  const locationName = service.mainLocation || service.location || '';
  const tabTitles = ['Summary', 'Reported Info', 'Location', 'Attachments', 'Service History'];

  return (
    <PageContainer>
      <HeaderHeroWrapper>
        <PageHeader>
          <IconButton onClick={handleBack} sx={{ color: COLORS.White }} aria-label="Go back">
            <ArrowBack />
          </IconButton>
          <HeaderContent>
            <HeaderRow>
              <ServiceIdTitle>Service #{service.id}</ServiceIdTitle>
            </HeaderRow>
            <HeaderMeta>
              {locationName && <HeaderLocation>{locationName}</HeaderLocation>}
              {(service.priorityLabel || service.priority?.shortName) && (
                <PriorityBadge bgColor={service.priorityColor || service.priority?.color}>
                  {service.priorityLabel || service.priority?.shortName}
                </PriorityBadge>
              )}
            </HeaderMeta>
          </HeaderContent>
        </PageHeader>
      </HeaderHeroWrapper>

      <MobileSLAHero service={service} serviceSLAs={detail.serviceSLAs} />

      <TabContainer>
        <ContentContainer>
          <Tabs
            name="service-detail"
            tabTitles={tabTitles}
            variant="scrollable"
            scrollButtons={false}
            swipeable
            keepMounted
            TabIndicatorProps={{
              style: {
                background: `linear-gradient(135deg, ${COLORS.NavyBlue} 0%, ${COLORS.NavyBlueDark} 100%)`,
                height: '3px',
                borderRadius: '2px',
              },
            }}
            tabSx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              '&.Mui-selected': { color: COLORS.NavyBlue, fontWeight: 600 },
            }}
          >
            <MobileServiceSummary
              service={service}
              logoPic={logoPic}
              nextStatuses={detail.nextStatuses}
              onStatusChanged={detail.handleStatusRefresh}
            />
            <MobileReportedInfo service={service} />
            <MobileLocationSection
              service={service}
              locationAttributes={detail.locationAttributes}
              tradingHours={detail.tradingHours}
              locationReferenceNumbers={detail.locationReferenceNumbers}
              locationContacts={detail.locationContacts}
              coverageHours={detail.coverageHours}
              isLoading={detail.isLoadingLocation}
            />
            <MobileAttachments
              serviceId={serviceId}
              attachments={detail.attachments}
              isLoading={detail.isLoadingAttachments}
              onRefresh={detail.handleAttachmentRefresh}
            />
            <MobileServiceHistory
              serviceLogs={detail.serviceLogs}
              isLoading={detail.isLoadingLogs}
            />
          </Tabs>
        </ContentContainer>
      </TabContainer>

      <MobileActionBar
        service={service}
        nextStatuses={detail.nextStatuses}
        partActionTypes={detail.partActionTypes}
        permissions={getUserPermissionsQuery.data}
        onTakePhoto={actions.handleTakePhoto}
        onUploadFiles={actions.handleUploadFiles}
        onUploadError={actions.handleUploadError}
        onStatusChanged={detail.handleStatusRefresh}
        onNoteAdded={actions.handleNoteAdded}
        onPartActionComplete={actions.handlePartActionComplete}
      />

      <Snackbar
        open={!!actions.errorMessage}
        autoHideDuration={6000}
        onClose={actions.handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 10 }}
      >
        <Alert onClose={actions.handleCloseError} severity="error" sx={{ width: '100%' }}>
          {actions.errorMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!actions.successMessage}
        autoHideDuration={4000}
        onClose={actions.handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 10 }}
      >
        <Alert onClose={actions.handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {actions.successMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};
